// Lightweight anchored popover. Used by the composer to float the
// emoji/GIF/sticker picker above the Smile trigger inside the input
// pill so the popover stays centered on the button regardless of how
// wide the composer surface gets.
//
// Strategy:
//   - Caller wraps the trigger element with `<PopoverAnchor>` which
//     forwards a ref to the inner View.
//   - When `visible` becomes true, the popover measures the anchor
//     in window coordinates, then renders its content inside a
//     full-screen `<Modal transparent>` positioned absolutely
//     above the anchor. Backdrop press closes the popover.
//   - `placement="top"` (default) places the popover ABOVE the
//     anchor; `placement="bottom"` flips it below.
//   - `align="center"` (default) horizontally centers the content on
//     the anchor; `start` / `end` clamp to the anchor's left/right
//     edge.
//   - The popover content auto-clamps to the viewport so it doesn't
//     spill off-screen even on narrow widths.
//
// Compatible with react-native-web (apps/web) and react-native-macos
// (apps/desktop). On native iOS/Android the same `Modal` primitive
// keeps the popover above the keyboard.

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  View,
  type ViewStyle,
} from 'react-native';
// `react-dom`'s portal escapes Modal-style pointer-event capture on
// react-native-web (the Modal wrapper has `pointer-events: auto` and
// intercepts taps even when the backdrop sets `pointerEvents:
// 'box-none'`); for non-dismissable popovers we portal to
// `document.body`. The portal resolution is platform-split: web gets
// react-dom's `createPortal`, native gets `null` and falls back to
// inline / Modal rendering. The split is REQUIRED — RN-macOS has a
// partial `document` global, so a `typeof document` guard here wrongly
// passes and Metro then can't resolve the `require('react-dom')`
// ("Requiring unknown module undefined").
import { createDomPortal } from './domPortal';

interface AnchorRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface PopoverAnchorHandle {
  /** Measure the anchor in window coordinates. Resolves null on failure. */
  measure(): Promise<AnchorRect | null>;
}

interface PopoverAnchorProps {
  readonly children: React.ReactNode;
  /**
   * Style applied to the wrapping `<View>`. Use this to forward flex
   * behavior from the parent (e.g. `flex: 1` so the anchor doesn't
   * collapse to its intrinsic content width).
   */
  readonly style?: ViewStyle | undefined;
}

/**
 * Wrap your trigger with this so the popover can measure it. Forwards
 * a ref that exposes a `measure()` method returning the window-space
 * rect. Renders the children inside a `<View>` that participates in
 * the normal layout flow.
 */
export const PopoverAnchor = forwardRef<PopoverAnchorHandle, PopoverAnchorProps>(
  ({ children, style }, ref) => {
    const inner = useRef<View | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        measure: () =>
          new Promise<AnchorRect | null>((resolve) => {
            const node = inner.current;
            if (node === null) {
              resolve(null);
              return;
            }
            node.measureInWindow((x, y, width, height) => {
              if (
                typeof x !== 'number' ||
                typeof y !== 'number' ||
                typeof width !== 'number' ||
                typeof height !== 'number'
              ) {
                resolve(null);
                return;
              }
              resolve({ x, y, width, height });
            });
          }),
      }),
      [],
    );

    return (
      <View ref={inner} collapsable={false} style={style}>
        {children}
      </View>
    );
  },
);
PopoverAnchor.displayName = 'PopoverAnchor';

export type PopoverPlacement = 'top' | 'bottom';
export type PopoverAlign = 'start' | 'center' | 'end';

export interface PopoverProps {
  readonly visible: boolean;
  readonly anchorRef: React.RefObject<PopoverAnchorHandle | null>;
  readonly onClose: () => void;
  readonly placement?: PopoverPlacement | undefined;
  readonly align?: PopoverAlign | undefined;
  /**
   * When true (default) the popover flips to the opposite side if the preferred
   * `placement` doesn't fit the viewport. When false it NEVER flips: it stays on
   * the requested side and clamps against the viewport edge instead. Use
   * `flip={false}` with `placement="top"` for a popover that must ALWAYS open
   * upward (e.g. the composer emoji/GIF picker), even in a short viewport.
   */
  readonly flip?: boolean | undefined;
  /** Pixel offset between anchor edge and popover edge. */
  readonly gap?: number | undefined;
  /** Padding clamped against the viewport edges. */
  readonly screenPadding?: number | undefined;
  /**
   * When true (default) the backdrop captures touches and closes the
   * popover on outside-press. When false the backdrop lets pointer
   * events pass through so the popover floats above other content
   * without trapping clicks — useful for "fixed-visible" popovers
   * (e.g. a formatting toolbar bound to the editor's focus state).
   */
  readonly dismissOnBackdropPress?: boolean | undefined;
  readonly children: React.ReactNode;
  readonly testID?: string | undefined;
}

interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

function readViewportSize(): ViewportSize {
  // Use the DOM viewport on react-native-web; fall back to a sane
  // default on native (the popover will still re-measure once
  // `onLayout` fires).
  if (typeof window !== 'undefined' && window.innerWidth) {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  return { width: 1280, height: 720 };
}

export const Popover: React.FC<PopoverProps> = ({
  visible,
  anchorRef,
  onClose,
  placement = 'top',
  align = 'center',
  flip = true,
  gap = 8,
  screenPadding = 8,
  dismissOnBackdropPress = true,
  children,
  testID,
}) => {
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const [contentSize, setContentSize] = useState<{ width: number; height: number } | null>(null);

  // Measure the anchor whenever the popover opens. We don't re-measure
  // continuously — the anchor position is captured once per open
  // (matching native popover semantics).
  useEffect(() => {
    if (!visible) {
      setAnchor(null);
      setContentSize(null);
      return;
    }
    let cancelled = false;
    void anchorRef.current?.measure().then((rect) => {
      if (!cancelled && rect !== null) setAnchor(rect);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, anchorRef]);

  const onContentLayout = useCallback(
    (event: { nativeEvent: { layout: { width: number; height: number } } }) => {
      const { width, height } = event.nativeEvent.layout;
      // Only swap when the size actually changes so we don't re-render
      // on every paint.
      setContentSize((prev) =>
        prev !== null && prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    },
    [],
  );

  // Plain-DOM ref handler used by the portal path on web. The
  // RN-Web `<View>`'s `onLayout` isn't available on a plain `<div>`
  // wrapper, so we read `getBoundingClientRect()` once the ref
  // attaches and feed the same `setContentSize` state machine.
  const onContentLayoutDivRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node === null) return;
      const rect = node.getBoundingClientRect();
      setContentSize((prev) =>
        prev !== null && prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height },
      );
    },
    [],
  );

  if (!visible) return null;

  // Compute final on-screen position. While the content hasn't been
  // measured yet we render at opacity 0 to avoid a flash at (0,0).
  // `position` is set in the style array below (different value on
  // web vs native), so we only set `top`/`left`/`opacity` here.
  let positionStyle: ViewStyle = { opacity: 0 };
  if (anchor !== null && contentSize !== null) {
    const viewport = readViewportSize();
    const cw = contentSize.width;
    const ch = contentSize.height;

    let top: number;
    if (placement === 'top') {
      top = anchor.y - ch - gap;
      if (top < screenPadding) {
        // Not enough room above. Flip below only when flipping is allowed;
        // otherwise stay ABOVE and clamp to the top viewport edge so the
        // popover never opens downward (it may overlap the anchor on a very
        // short viewport, which is the intended "always upward" trade-off).
        top = flip ? anchor.y + anchor.height + gap : screenPadding;
      }
    } else {
      top = anchor.y + anchor.height + gap;
      if (top + ch + screenPadding > viewport.height) {
        if (flip) {
          const flipped = anchor.y - ch - gap;
          if (flipped >= screenPadding) top = flipped;
        } else {
          // Never flip up: clamp to the bottom viewport edge.
          top = Math.max(screenPadding, viewport.height - ch - screenPadding);
        }
      }
    }

    let left: number;
    if (align === 'start') {
      left = anchor.x;
    } else if (align === 'end') {
      left = anchor.x + anchor.width - cw;
    } else {
      left = anchor.x + anchor.width / 2 - cw / 2;
    }
    left = Math.max(
      screenPadding,
      Math.min(left, viewport.width - cw - screenPadding),
    );

    positionStyle = {
      top,
      left,
      opacity: 1,
    };
  }

  // The popover's interactive surface. Used by BOTH render paths.
  // `position: fixed` (web) anchors the layer to viewport coords,
  // which matches `measureInWindow`. On native this falls back to
  // `position: absolute` since RN doesn't support `'fixed'`.
  // The popover content tree (without the outer positioning layer).
  // Both the Modal path and the portal path embed this same subtree.
  const contentInner = (
    <View
      // Inner wrapper traps presses on the actual popover content
      // so they don't bubble to the backdrop (when dismissable) or
      // the page (when not). Plain `View` with a responder
      // handler — NO `Pressable`, so children's buttons stay valid
      // DOM.
      style={{ pointerEvents: 'auto' as const }}
      onStartShouldSetResponder={() => true}
      onResponderTerminationRequest={() => false}
    >
      {children}
    </View>
  );

  // Non-dismissable + web: bypass Modal entirely. React-Native-Web's
  // Modal wraps content in a `<div>` with `pointer-events: auto`
  // that captures every tap over its area, blocking the underlying
  // input. We portal a plain `<div style="position: fixed">`
  // straight to `document.body` so the only interactive region is
  // the popover content itself. Using a real `<div>` here (rather
  // than RN-Web's `<View>`) guarantees `position: fixed` applies —
  // `View`'s style normalizer silently drops the unknown value.
  if (!dismissOnBackdropPress) {
    if (createDomPortal !== null && typeof document !== 'undefined') {
      // `positionStyle` carries `top` / `left` / `opacity`; we layer
      // the fixed-position + box-none rules on top via plain CSS.
      const styleProps = positionStyle as {
        readonly top?: number;
        readonly left?: number;
        readonly opacity?: number;
      };
      const fixedStyle: React.CSSProperties = {
        position: 'fixed',
        top: typeof styleProps.top === 'number' ? `${styleProps.top}px` : undefined,
        left: typeof styleProps.left === 'number' ? `${styleProps.left}px` : undefined,
        opacity: styleProps.opacity ?? 1,
        // Empty gutter around the chip lets clicks pass through to
        // whatever sits underneath (the chat composer's input).
        pointerEvents: 'none',
        zIndex: 9999,
      };
      const innerDivStyle: React.CSSProperties = {
        pointerEvents: 'auto',
      };
      return createDomPortal(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.createElement(
          'div',
          { style: fixedStyle, 'data-testid': testID },
          React.createElement(
            'div',
            { style: innerDivStyle, ref: onContentLayoutDivRef },
            contentInner,
          ),
        ) as React.ReactElement,
        document.body,
      );
    }
    // Native fallback — render inline with absolute positioning.
    // `position: 'absolute'` + `pointerEvents: 'box-none'` cannot be
    // expressed via NativeWind, so they stay inline.
    return (
      <View
        style={[
          { position: 'absolute' as const },
          positionStyle,
          { pointerEvents: 'box-none' as const },
        ]}
        onLayout={onContentLayout}
        testID={testID}
      >
        {contentInner}
      </View>
    );
  }

  // Dismissable path keeps the React Native chain (Modal+View) so
  // the backdrop can capture outside-press for close-on-tap. The
  // positioning + pointerEvents rules can't be expressed via
  // NativeWind so they stay inline.
  const contentNode = (
    <View
      style={[
        { position: 'absolute' as const },
        positionStyle,
        { pointerEvents: 'box-none' as const },
      ]}
      onLayout={onContentLayout}
      testID={testID}
    >
      {contentInner}
    </View>
  );

  return (
    <Modal
      transparent
      visible
      animationType="none"
      onRequestClose={onClose}
      // On web (`react-native-web`) Modal is rendered into a portal at
      // document.body, which is exactly what we want — the popover
      // sits above every other layer including the composer's
      // scrolling message list.
      hardwareAccelerated
    >
      {/*
        Backdrop. Tappable — closing the popover when the user taps
        outside the content. We use a `View` (not `Pressable`) so it
        doesn't render as a `<button>` — content below contains real
        buttons and nesting `<button>` inside `<button>` is invalid
        HTML.
      */}
      <View
        accessibilityRole="none"
        // Fully-transparent backdrop — the popover is visible above
        // the surface but the rest of the chat stays unobscured.
        className="flex-1 bg-transparent"
        onStartShouldSetResponder={() => true}
        onResponderRelease={onClose}
      >
        {contentNode}
      </View>
    </Modal>
  );
};
